// ABOUTME: Registry-driven admin editor drawer rendering labeled, per-integration config inputs.
import { useNavigate } from 'react-router-dom';
import { useUpdatePolling, useUpdateConfig } from '../../hooks/useAdminQueries';
import {
  getPersistableEditableFieldsForConfig,
  getPersistableEditableFieldsForPolling,
  getFieldDescription,
  RegistryField,
} from '../../constants/fieldDescriptions';
import { PollingItem, Config, ConfigPatchFields, PollingPatchFields } from '../../api/adminApi';
import { ChangeHistoryList } from './ChangeHistoryList';
import { FieldTooltip } from '../common/FieldTooltip';
import { useState, useEffect, useMemo } from 'react';
import { clientTransformationPathConfig } from '../../../../transmormer/globals/metadata-registry';
import { Source, Destination, EntityType } from '../../../../types/enums';
import { useIsSuperAdmin } from '../../hooks/useRole';
import './EditDrawer.css';

interface EditDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'polling' | 'config' | null;
  data: PollingItem | Config | null;
}

type FieldValue = boolean | string | string[];

// Read the config's stored value into the input shape for a field type.
function normalizeValue(field: RegistryField, raw: unknown): FieldValue {
  switch (field.type) {
    case 'toggle':
      return raw === true;
    case 'multiselect':
      return Array.isArray(raw) ? (raw as string[]) : [];
    case 'select': {
      const fallback = field.defaultValue ?? field.options?.[0] ?? '';
      if (raw == null) return fallback;
      // Legacy configs stored closeJourneyLog as a boolean (true = closed). A
      // controlled select cannot display a value outside its options, so map
      // any out-of-options value to a valid option rather than a phantom.
      const candidate =
        typeof raw === 'boolean' ? (raw ? 'closed' : 'open') : String(raw);
      return field.options?.includes(candidate) ? candidate : fallback;
    }
    case 'number':
      return raw != null ? String(raw) : field.defaultValue ?? '';
    default:
      return raw != null ? String(raw) : '';
  }
}

function valuesEqual(a: FieldValue, b: FieldValue): boolean {
  if (Array.isArray(a) || Array.isArray(b)) {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return a === b;
}

export function EditDrawer({ isOpen, onClose, type, data }: EditDrawerProps) {
  const navigate = useNavigate();
  const isSuperAdmin = useIsSuperAdmin();
  const updatePolling = useUpdatePolling();
  const updateConfig = useUpdateConfig();

  const [values, setValues] = useState<Record<string, FieldValue>>({});
  const [initial, setInitial] = useState<Record<string, FieldValue>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'edit' | 'history'>('edit');

  // Editable config fields scoped to this config's integration path and
  // accepted by PATCH /v2/api/admin/configs/:id.
  const configFields = useMemo<RegistryField[]>(() => {
    if (type !== 'config' || !data) return [];
    const config = data as Config;
    try {
      return getPersistableEditableFieldsForConfig(
        config.origin,
        config.destination,
        config.entity,
      );
    } catch {
      return [];
    }
  }, [type, data]);

  // The schema editor sub-route only renders for configs whose transformation
  // path is registered client-side; without one the page dead-ends on an empty
  // state, so the affordance is hidden rather than offered. Access is further
  // restricted to super admins, matching the route guard on the schema page.
  const hasTransformationPath = useMemo<boolean>(() => {
    if (type !== 'config' || !data) return false;
    const config = data as Config;
    return (
      clientTransformationPathConfig(
        config.origin as Source,
        config.destination as Destination,
        config.entity as EntityType,
      ) !== undefined
    );
  }, [type, data]);

  const pollingFields = useMemo<RegistryField[]>(() => {
    if (type !== 'polling' || !data) return [];
    const polling = data as PollingItem;
    try {
      return getPersistableEditableFieldsForPolling(
        polling.origin,
        polling.entity,
      );
    } catch {
      return [];
    }
  }, [type, data]);

  const editableFields = type === 'config' ? configFields : pollingFields;

  useEffect(() => {
    if (!data || !type) return;
    setSaveError(null);
    setActiveTab('edit');

    const seeded: Record<string, FieldValue> = {};
    for (const field of editableFields) {
      const source = data as PollingItem | Config;
      seeded[field.key] = normalizeValue(field, source[field.key]);
    }
    setValues(seeded);
    setInitial(seeded);
  }, [data, type, editableFields]);

  const setField = (key: string, value: FieldValue) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const serializeFieldValue = (
    field: RegistryField,
    value: FieldValue,
  ): FieldValue | number => {
    if (field.type === 'number') {
      const trimmed = String(value).trim();
      if (trimmed === '') return '';
      return Number(trimmed);
    }
    return value;
  };

  // Reject values the backend PATCH schema would reject, before sending, so a
  // non-technical user gets a clear inline message instead of a failed request.
  const validateFieldValue = (
    field: RegistryField,
    value: FieldValue,
  ): string | null => {
    if (field.type === 'number') {
      const trimmed = String(value).trim();
      if (trimmed === '') return `${field.label} is required.`;
      const num = Number(trimmed);
      if (!Number.isFinite(num)) return `${field.label} must be a number.`;
      if (field.step === 1 && !Number.isInteger(num)) {
        return `${field.label} must be a whole number.`;
      }
      if (field.min !== undefined && num < field.min) {
        return `${field.label} must be at least ${field.min}.`;
      }
      if (field.max !== undefined && num > field.max) {
        return `${field.label} must be at most ${field.max}.`;
      }
      return null;
    }

    if (field.required && field.type === 'text') {
      if (String(value).trim() === '') {
        return `${field.label} is required.`;
      }
    }

    return null;
  };

  const firstValidationError = (fields: RegistryField[]): string | null => {
    for (const field of fields) {
      const next = values[field.key];
      if (next === undefined) continue;
      if (valuesEqual(next, initial[field.key])) continue;
      const error = validateFieldValue(field, next);
      if (error) return error;
    }
    return null;
  };

  // A field may be gated by another field's value in the same config. When the
  // gate is unmet the input is disabled (never hidden) so the setting stays
  // visible, and a note names the field that must change first.
  const isFieldEnabled = (field: RegistryField): boolean => {
    if (!field.enabledWhen) return true;
    return values[field.enabledWhen.field] === field.enabledWhen.equals;
  };

  const handleSave = async () => {
    if (!data) return;

    const validationError = firstValidationError(editableFields);
    if (validationError) {
      setSaveError(validationError);
      return;
    }

    // Mirror backend cross-field rule: non-ATA defect exports need a signee.
    // Only enforce where issueSignee is an editable field for this config;
    // flight/uplift configs render useATASpec but never issueSignee.
    const hasIssueSignee = editableFields.some(
      (field) => field.key === 'issueSignee',
    );
    if (type === 'config' && hasIssueSignee && values.useATASpec === false) {
      const signee = String(values.issueSignee ?? '').trim();
      if (!signee) {
        setSaveError('Issue Signee is required when Use ATA Spec is off.');
        return;
      }
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      if (type === 'polling') {
        const payload: Record<string, FieldValue | number> = {};
        for (const field of pollingFields) {
          const next = values[field.key];
          if (next === undefined) continue;
          // A gate-disabled field is not applicable; never persist its value.
          if (!isFieldEnabled(field)) continue;
          if (!valuesEqual(next, initial[field.key])) {
            payload[field.key] = serializeFieldValue(field, next);
          }
        }

        if (Object.keys(payload).length > 0) {
          await updatePolling.mutateAsync({
            id: data.id,
            data: payload as PollingPatchFields,
          });
        }
        onClose();
        return;
      }

      // Send only changed fields.
      const payload: Record<string, FieldValue | number> = {};
      for (const field of configFields) {
        const next = values[field.key];
        if (next === undefined) continue;
        // A gate-disabled field is not applicable; never persist its value.
        if (!isFieldEnabled(field)) continue;
        if (!valuesEqual(next, initial[field.key])) {
          payload[field.key] = serializeFieldValue(field, next);
        }
      }

      if (Object.keys(payload).length > 0) {
        await updateConfig.mutateAsync({
          id: data.id,
          data: payload as ConfigPatchFields,
        });
      }
      onClose();
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : 'Failed to save changes',
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const gateNote = (field: RegistryField): string | null => {
    if (!field.enabledWhen) return null;
    const { field: gateKey, equals } = field.enabledWhen;
    const label = getFieldDescription(gateKey)?.label ?? gateKey;
    const shown =
      equals === true ? 'On' : equals === false ? 'Off' : String(equals);
    return `Requires ${label}: ${shown}`;
  };

  const renderInput = (field: RegistryField, enabled: boolean) => {
    const value = values[field.key];

    if (field.type === 'toggle') {
      return (
        <label className="drawer-toggle">
          <input
            type="checkbox"
            aria-label={field.label}
            checked={value === true}
            disabled={!enabled}
            onChange={(e) => setField(field.key, e.target.checked)}
          />
          <span
            className={`toggle-label ${value === true ? 'status-on' : 'status-off'}`}
          >
            {value === true ? 'On' : 'Off'}
          </span>
        </label>
      );
    }

    if (field.type === 'select') {
      return (
        <select
          className="drawer-input"
          aria-label={field.label}
          value={String(value ?? '')}
          disabled={!enabled}
          onChange={(e) => setField(field.key, e.target.value)}
        >
          {(field.options ?? []).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      );
    }

    if (field.type === 'multiselect') {
      const selected = Array.isArray(value) ? value : [];
      if (field.options && field.options.length > 0) {
        return (
          <div className="drawer-checkbox-group">
            {field.options.map((option) => (
              <label key={option} className="drawer-checkbox">
                <input
                  type="checkbox"
                  aria-label={`${field.label}: ${option}`}
                  checked={selected.includes(option)}
                  disabled={!enabled}
                  onChange={(e) =>
                    setField(
                      field.key,
                      e.target.checked
                        ? [...selected, option]
                        : selected.filter((item) => item !== option),
                    )
                  }
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        );
      }
      return (
        <input
          type="text"
          className="drawer-input"
          aria-label={field.label}
          value={selected.join(', ')}
          placeholder="Comma-separated values"
          disabled={!enabled}
          onChange={(e) =>
            setField(
              field.key,
              e.target.value
                .split(',')
                .map((item) => item.trim())
                .filter(Boolean),
            )
          }
        />
      );
    }

    return (
      <input
        type={field.type === 'number' ? 'number' : 'text'}
        className="drawer-input"
        aria-label={field.label}
        value={value === undefined ? '' : String(value)}
        disabled={!enabled}
        min={field.type === 'number' ? field.min : undefined}
        max={field.type === 'number' ? field.max : undefined}
        step={field.type === 'number' ? field.step : undefined}
        onChange={(e) => setField(field.key, e.target.value)}
      />
    );
  };

  const config = type === 'config' ? (data as Config) : null;
  const polling = type === 'polling' ? (data as PollingItem) : null;

  return (
    <>
      <div className="edit-drawer-overlay" onClick={onClose}></div>
      <div className={`edit-drawer ${isOpen ? 'open' : ''}`}>
        <div className="edit-drawer-header">
          <h2>Edit {type === 'polling' ? 'Polling Item' : 'Configuration'}</h2>
          <button className="edit-drawer-close" onClick={onClose}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
            >
              <path
                d="M4 4L16 16M16 4L4 16"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="edit-drawer-tabs" role="tablist">
          <button
            role="tab"
            aria-selected={activeTab === 'edit'}
            className={`edit-drawer-tab ${activeTab === 'edit' ? 'active' : ''}`}
            onClick={() => setActiveTab('edit')}
          >
            Edit
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'history'}
            className={`edit-drawer-tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            History
          </button>
        </div>

        {activeTab === 'history' && data && (
          <div className="edit-drawer-content">
            <ChangeHistoryList
              collection={type === 'polling' ? 'polling' : 'configs'}
              documentId={data.id}
            />
          </div>
        )}

        {activeTab === 'edit' && (
        <div className="edit-drawer-content">
          {polling && (
            <div className="edit-form">
              <div className="drawer-section-readonly">
                <h3 className="drawer-section-title">Details</h3>
                <div className="drawer-field-row-readonly">
                  <label>Origin</label>
                  <span>{polling.origin}</span>
                </div>
                <div className="drawer-field-row-readonly">
                  <label>Entity</label>
                  <span>{polling.entity}</span>
                </div>
                {polling.url && (
                  <div className="drawer-field-row-readonly">
                    <label>API URL</label>
                    <span>{polling.url}</span>
                  </div>
                )}
              </div>

              <div className="drawer-section-editable">
                <h3 className="drawer-section-title">Settings</h3>
                {pollingFields.length === 0 ? (
                  <p className="drawer-empty">
                    No editable settings for this integration.
                  </p>
                ) : (
                  pollingFields.map((field) => {
                    const enabled = isFieldEnabled(field);
                    const note = enabled ? null : gateNote(field);
                    return (
                      <div
                        key={field.key}
                        className={`drawer-field ${enabled ? '' : 'drawer-field-gated'}`}
                      >
                        <span className="drawer-field-label">
                          {field.label}
                          <FieldTooltip fieldKey={field.key} />
                        </span>
                        {renderInput(field, enabled)}
                        {note && <span className="drawer-field-note">{note}</span>}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {config && (
            <div className="edit-form">
              <div className="drawer-section-readonly">
                <h3 className="drawer-section-title">Details</h3>
                <div className="drawer-field-row-readonly">
                  <label>Origin</label>
                  <span>{config.origin}</span>
                </div>
                <div className="drawer-field-row-readonly">
                  <label>Destination</label>
                  <span>{config.destination}</span>
                </div>
                <div className="drawer-field-row-readonly">
                  <label>Entity</label>
                  <span>{config.entity}</span>
                </div>
              </div>

              {hasTransformationPath && isSuperAdmin && (
                <div className="drawer-section-readonly">
                  <h3 className="drawer-section-title">Transformation Schema</h3>
                  <button
                    type="button"
                    className="drawer-schema-btn"
                    onClick={() => {
                      onClose();
                      navigate(`/admin/config/${config.id}/schema`);
                    }}
                  >
                    Edit Schema
                  </button>
                </div>
              )}

              <div className="drawer-section-editable">
                <h3 className="drawer-section-title">Settings</h3>
                {configFields.length === 0 ? (
                  <p className="drawer-empty">
                    No editable settings for this integration.
                  </p>
                ) : (
                  configFields.map((field) => {
                    const enabled = isFieldEnabled(field);
                    const note = enabled ? null : gateNote(field);
                    return (
                      <div
                        key={field.key}
                        className={`drawer-field ${enabled ? '' : 'drawer-field-gated'}`}
                      >
                        <span className="drawer-field-label">
                          {field.label}
                          <FieldTooltip fieldKey={field.key} />
                        </span>
                        {renderInput(field, enabled)}
                        {note && <span className="drawer-field-note">{note}</span>}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
        )}

        {activeTab === 'edit' && (
        <div className="edit-drawer-footer">
          {saveError && <span className="drawer-error">{saveError}</span>}
          <button className="btn-cancel" onClick={onClose} disabled={isSaving}>
            Cancel
          </button>
          <button className="btn-save" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
        )}
      </div>
    </>
  );
}
